import React, { RefObject, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Button from "./Common/Button";
import EditingIcon from "../Icons/EditingIcon";
import useAfterMountEffect from "../Hooks/useAfterMountEffect";

export const FieldInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  background: transparent;
  color: var(--color-text-light);
  font-size: unset;
  border: solid transparent;
  padding: 0.5em;
  margin: 0.5em;
  font-family: inherit;
  line-height: 1.5em;
`;

export const EditableFieldInput = styled(FieldInput)<{
  isEditing: boolean;
  disabled?: boolean;
}>`
  ${({ disabled }) => !disabled && `cursor: pointer;`}
  ${({ isEditing }) => isEditing && `border: solid var(--color-text-light);`}
  :hover {
    border: solid var(--color-text-light);
  }
  :focus {
    border: solid var(--color-text-light);
  }
`;
export const SmallFieldInput = styled(FieldInput)<{
  isEditing: boolean;
}>`
  max-width: 3.3em;
  font-size: 0.8em;
  line-height: unset;
  padding-top: 0.6em;
`;

const EditButtons = styled.span<{ isEditing: boolean }>`
  visibility: hidden;
  ${({ isEditing }) => isEditing && `visibility: visible;`}
`;

const StyledButton = styled(Button)`
  margin: 0.2em;
`;

interface EditableFieldWithConfirmationProps {
  type: string;
  placeholder: string;
  category: string;
  index: number;
  editingSocketName?: string;
  handleOnCategoryDone: (index: number, value: string) => void;
  handleOnCategoryChange: (index: number, isEditing: boolean) => void;
}

export const EditableFieldWithConfirmation = ({
  type,
  category,
  index,
  editingSocketName,
  handleOnCategoryDone,
  handleOnCategoryChange,
  ...rest
}: EditableFieldWithConfirmationProps) => {
  const [value, setValue] = useState(category);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleOnDiscardChanges = () => {
    setValue(category);
    setIsEditing(false);
  };
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setIsEditing(true);
  };
  const handleOnDone = () => {
    handleOnCategoryDone(index, value);
    setIsEditing(false);
    inputRef.current?.blur();
  };
  const handleOnKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleOnDone();
    }
  };
  useAfterMountEffect(() => {
    handleOnCategoryChange(index, isEditing);
  }, [isEditing, handleOnCategoryChange, index]);

  useEffect(() => {
    setValue(category);
    setIsEditing(false);
  }, [category]);
  return (
    <>
      <EditableField
        {...rest}
        inputRef={inputRef}
        type={type}
        value={value}
        onChange={handleOnChange}
        isEditing={isEditing}
        onKeyPress={handleOnKeyPress}
      />
      {editingSocketName && <EditingIcon />}
      <EditButtons isEditing={isEditing}>
        <StyledButton isSmall isSecondary onClick={handleOnDone}>
          ✔
        </StyledButton>
        <StyledButton isSmall isSecondary onClick={handleOnDiscardChanges}>
          ✖
        </StyledButton>
      </EditButtons>
    </>
  );
};

interface EditableFieldProps {
  inputRef: RefObject<HTMLInputElement>;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isEditing: boolean;
}

export const EditableField = ({
  inputRef,
  type,
  value,
  onChange,
  onKeyPress,
  isEditing,
  ...rest
}: EditableFieldProps) => {
  return (
    <EditableFieldInput
      {...rest}
      ref={inputRef}
      type={type}
      value={value}
      onChange={onChange}
      isEditing={isEditing}
      onKeyPress={onKeyPress}
    />
  );
};
